import { LazyQueryHookOptions, QueryHookOptions } from '@apollo/client';

import {
  ProductCategoriesQuery,
  ProductCategoriesQueryVariables,
  ProductCategoryFilter,
  ProductCategoryFragment,
  ProductCategoryQuery,
  ProductCategoryQueryVariables,
  useProductCategoriesLazyQuery,
  useProductCategoriesQuery,
  useProductCategoryQuery,
} from '@occc/graphql';
import { useChainId } from '@occc-app/Chain/hooks';

import { PaginationInfo, usePaginatedQuery } from '../../hooks/usePaginatedQuery';
import { useLazyQuery } from '../utils';

type Options = QueryHookOptions<ProductCategoryFragment[], ProductCategoryQueryVariables>;

interface GetProductCategoriesProps {
  filter?: ProductCategoryFilter;
  pageSize?: number;
  baseOptions?: Options;
  paginationInfo?: PaginationInfo;
  onPageChange?: (info: PaginationInfo) => void;
}

export const useGetProductCategoryById = (idEquals: string) => {
  const response = useProductCategoryQuery({
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    variables: {
      filter: {
        idEquals,
      },
    },
  });

  const data = response.data?.productCategories?.edges[0]?.node;

  return {
    ...response,
    data,
  };
};

const transform = (data: ProductCategoryQuery) => {
  const connection = data.productCategories;

  if (!connection) {
    return [];
  }

  const nodes = connection.edges.map((edge) => edge.node);
  return nodes;
};

export const useGetProductCategoriesByIds = (idContains: string[]) => {
  const response = useProductCategoryQuery({
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    skip: !idContains.length,
    variables: {
      filter: {
        idContains,
      },
    },
  });

  return {
    ...response,
    data: response.data ? transform(response.data) : response.data,
  };
};

export const useGetProductCategories = ({
  filter,
  pageSize,
  baseOptions,
  paginationInfo,
  onPageChange,
}: GetProductCategoriesProps = {}) => {
  const chainId = useChainId();

  const response = usePaginatedQuery(
    useProductCategoriesQuery,
    'productCategories.pageInfo',
    pageSize,
    {
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      variables: {
        filter: {
          chainIdEquals: chainId,
          ...filter,
        },
      },
      ...baseOptions,
      onCompleted: (data) => {
        if (baseOptions?.onCompleted) {
          baseOptions?.onCompleted(transform(data));
        }
      },
    } as QueryHookOptions,
    paginationInfo,
    onPageChange,
  );

  return {
    ...response,
    data: response.data ? transform(response.data) : response.data,
  };
};

export const useGetProductCategoriesLazy = (
  baseOptions?: LazyQueryHookOptions<ProductCategoriesQuery, ProductCategoriesQueryVariables>,
) => {
  const chainId = useChainId();

  const [executeQuery, response] = useLazyQuery(useProductCategoriesLazyQuery, baseOptions);

  return {
    executeQuery: (
      options?: LazyQueryHookOptions<ProductCategoriesQuery, ProductCategoriesQueryVariables>,
    ) =>
      executeQuery({
        ...options,
        variables: {
          ...options?.variables,
          filter: {
            ...options?.variables?.filter,
            chainIdEquals: chainId,
          },
        },
      }),
    response: {
      ...response,
      data: response.data ? transform(response.data) : response.data,
    },
  };
};
